import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Zap, Lock, Globe, Clock } from 'lucide-react';
import { CTAButton } from './ui/CTAButton';
import { trackAddToCart } from '../utils/metaPixel';
import { useCurrency } from '../hooks/useCurrency';
export function OfferSection() {
  const {
    price,
    currency,
    loading
  } = useCurrency();
  const [timeLeft, setTimeLeft] = useState(5 * 60); // 5 minutes in seconds
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const timerDisplay = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  const handleBuyClick = () => {
    trackAddToCart();
  };
  return <section id="offer" className="py-10 md:py-32 bg-maternal-cream relative overflow-hidden" data-id="element-118">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-maternal-amber via-maternal-orange to-maternal-gold" data-id="element-119"></div>
      <div className="absolute -left-32 -bottom-32 w-96 h-96 bg-maternal-amber/10 rounded-full blur-3xl" data-id="element-120"></div>
      <div className="absolute -right-32 -top-32 w-96 h-96 bg-maternal-orange/10 rounded-full blur-3xl" data-id="element-121"></div>

      <div className="container mx-auto px-4 max-w-4xl relative z-10" data-id="element-122">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="bg-white rounded-3xl shadow-2xl border-2 border-maternal-amber/30 p-6 md:p-12 text-center" data-id="element-123">
          <h2 className="text-3xl md:text-5xl font-bold text-maternal-brown mb-6" data-id="element-124">
            Recupera la paz en tu hogar hoy mismo
          </h2>

          <p className="text-lg md:text-xl text-maternal-lightBrown mb-8 max-w-2xl mx-auto" data-id="element-125">
            Por menos de lo que cuesta una salida a comer, puedes tener la hoja
            de ruta exacta que salvará la relación con tu hijo.
          </p>

          {/* Countdown Timer */}
          <motion.div animate={{
          opacity: [1, 0.5, 1],
          scale: [1, 1.03, 1]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="mb-8 inline-block" data-id="element-126">
            <div className="bg-red-600 text-white px-6 md:px-8 py-4 md:py-5 rounded-2xl shadow-lg shadow-red-600/30" data-id="element-127">
              <div className="flex items-center justify-center gap-3" data-id="element-128">
                <Clock className="w-6 h-6 md:w-7 md:h-7" data-id="element-129" />
                <span className="font-bold text-lg md:text-2xl tracking-wide" data-id="element-130">
                  {timeLeft > 0 ? `🔥 ¡OFERTA EXCLUSIVA! EXPIRA EN ${timerDisplay} 🔥` : '⚠️ ¡LA OFERTA HA EXPIRADO!'}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Price Block */}
          <div className="mb-10" data-id="element-131">
            <span className="text-sm font-bold uppercase tracking-wider text-maternal-orange mb-3 block" data-id="element-132">
              Precio Exclusivo
            </span>

            {/* Original price - strikethrough */}
            <div className="flex items-center justify-center mb-1" data-id="element-133">
              <span className="text-2xl md:text-3xl text-maternal-lightBrown/50 line-through font-bold" data-id="element-134">
                $24.990 ARS
              </span>
            </div>

            {/* Discounted price */}
            <div className="flex items-center justify-center" data-id="element-135">
              {loading ? <span className="text-5xl md:text-7xl font-bold text-maternal-brown animate-pulse" data-id="element-136">
                  ...
                </span> : <>
                  <span className="text-5xl md:text-7xl font-bold text-maternal-brown" data-id="element-137">
                    ${price}
                  </span>
                  <span className="text-xl md:text-2xl text-maternal-lightBrown ml-2 mt-4" data-id="element-138">
                    {currency}
                  </span>
                </>}
            </div>

            {/* Savings badge */}
            <div className="mt-3 inline-block bg-green-100 text-green-700 font-bold text-sm px-4 py-1.5 rounded-full" data-id="element-139">
              ¡Ahorrás un 60%!
            </div>

            {currency !== 'ARS' && !loading && <div className="flex items-center justify-center mt-2 text-sm text-maternal-lightBrown/70" data-id="element-140">
                <Globe className="w-3.5 h-3.5 mr-1" data-id="element-141" />
                <span data-id="element-142">Precio convertido automáticamente a tu moneda local</span>
              </div>}
          </div>

          <div className="mb-8" data-id="element-143">
            <CTAButton className="text-lg md:text-xl py-5 px-10 w-full md:w-auto" href="https://checkout.ilumente.online/cart/52116586987842:1" external onClick={handleBuyClick} data-id="element-144" />
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 text-sm text-maternal-lightBrown bg-maternal-beige/50 p-4 rounded-xl" data-id="element-145">
            <div className="flex items-center" data-id="element-146">
              <Lock className="w-5 h-5 text-green-600 mr-2" data-id="element-147" />
              <span data-id="element-148">Compra 100% segura</span>
            </div>
            <div className="hidden md:block w-1 h-1 bg-maternal-amber rounded-full" data-id="element-149"></div>
            <div className="flex items-center" data-id="element-150">
              <Zap className="w-5 h-5 text-maternal-orange mr-2" data-id="element-151" />
              <span data-id="element-152">Acceso inmediato 24/7</span>
            </div>
            <div className="hidden md:block w-1 h-1 bg-maternal-amber rounded-full" data-id="element-153"></div>
            <div className="flex items-center" data-id="element-154">
              <ShieldCheck className="w-5 h-5 text-blue-600 mr-2" data-id="element-155" />
              <span data-id="element-156">Garantía de satisfacción</span>
            </div>
          </div>

          <p className="mt-6 text-sm text-maternal-lightBrown/70 italic" data-id="element-157">
            * Acceso inmediato a todo el material, incluso si son las 3 de la
            mañana.
          </p>
        </motion.div>
      </div>
    </section>;
}